/**
 * Entry of the script `lit-devtools dev` serves at `/lit-devtools.js`.
 *
 * Bundled as a classic IIFE (see `src/standalone/vite.config.ts`) so a page
 * that is not served by Vite, and may be on another origin, can load it with a
 * plain `<script src>`: classic scripts load cross-origin without CORS.
 * Importing the install modules starts the capture layers and the inspector;
 * {@link connectToDevServer} then attaches the RPC carrier they announce
 * themselves through.
 *
 * The server is wherever this script came from: behind a proxy or tunnel
 * (StackBlitz, Codespaces, ngrok) that is the public address the browser used,
 * not the one the server sees itself on. `globalThis.__LIT_DEVTOOLS_CONNECT__`,
 * which the server prepends to the script, carries its connection descriptor,
 * so this page never has to `fetch` `__connection.json` (a cross-origin fetch
 * the server does not answer with CORS headers). The descriptor holds paths
 * only, so it resolves against either address. Its `url` is the fallback for
 * when the script's own address is unknown.
 *
 * The page also gets the element picker, in its `lit` mode: there is no
 * source metadata to find components by, so any Lit element can be picked.
 * The panel is a separate tab here, and a page cannot raise another tab, so a
 * pick opens the panel by a fixed window name instead (see {@link pickInto}).
 */

import {installDefineSites} from './define-sites.js';
import {installTemplateSites} from './template-sites.js';
import {installInternalsHook} from './element-internals.js';
import {forgetOwnLit} from './own-lit.js';
import './inspector/persist.js';
import './timeline/install.js';
import './inspector/install.js';
import {initSourceOverlay} from './source-overlay/overlay-element.js';
import {createDock, type Dock} from './dock.js';
import {pickInto} from './panel-window.js';
import {connectToDevServer} from './rpc-transport.js';
import {joinPage} from './runtime-registry.js';
import type {ConnectOptions} from './rpc-transport.js';

joinPage('standalone');
// Where each class is defined comes from the stack of its define call, so
// this has to run before the page's scripts do: only defines after it are
// seen, hence the script belongs in <head>, ahead of the app. (Our own
// imports' defines are not the page's and need no record.)
installDefineSites();
// Which template rendered an element, likewise: lit-html reports its
// templates only to a hook that is set before it loads.
installTemplateSites();
// Custom states and form values live in ElementInternals, which only the
// element holds: the hook has to be there when it attaches them.
installInternalsHook();
// The picker's LitElement brought a Lit of our own: not the page's to count.
forgetOwnLit();

interface StandaloneConfig {
  url?: string;
  connectionMeta?: ConnectOptions['connectionMeta'];
  /** Dock the panel in the page; see `dock.ts`. */
  dock?: boolean;
}

const config =
  (globalThis as {__LIT_DEVTOOLS_CONNECT__?: StandaloneConfig})
    .__LIT_DEVTOOLS_CONNECT__ ?? {};

const script = document.currentScript;
const url =
  script instanceof HTMLScriptElement && script.src !== ''
    ? new URL('.', script.src).href
    : config.url;

if (url === undefined) {
  console.error(
    '[lit-devtools] cannot tell which dev server to connect to: load this ' +
      'script from the server with <script src="http://localhost:5180/lit-devtools.js">.'
  );
} else {
  let docked: (dock: Dock) => void = () => {};
  const dockReady = new Promise<Dock>((resolve) => (docked = resolve));
  const startPicker = () => {
    // Docked, a pick opens the drawer on the element; otherwise it raises
    // the panel's own tab.
    const dock = config.dock === true ? createDock(url) : undefined;
    if (dock !== undefined) docked(dock);
    initSourceOverlay({
      hosts: 'lit',
      onPick: pickInto(url, dock && ((href) => dock.show(href))),
    });
  };
  // Usually loaded from <head>, before there is a <body> to attach to.
  if (document.body === null) {
    document.addEventListener('DOMContentLoaded', startPicker, {once: true});
  } else {
    startPicker();
  }
  connectToDevServer(url, {
    connectionMeta: config.connectionMeta,
    // In a frame the page cannot prompt for the code; the dock asks instead.
    ...(config.dock === true
      ? {
          onAuthRequired: (auth) =>
            void dockReady.then((dock) => dock.askForCode(auth)),
        }
      : {}),
  }).then(
    () => console.info(`[lit-devtools] connected to ${url}`),
    (error) =>
      console.error(
        `[lit-devtools] could not connect to ${url}. Is \`lit-devtools dev\` ` +
          `running, and does it allow this page's origin (--allow-origin)?`,
        error
      )
  );
}
